import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { agentAppAPI } from '../../services/api';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

// Matches AgentCustomerListPage.js's own sessionStorage shape — presetting it here before
// navigating there means the list picks up the filter on mount exactly as if the agent had
// set it via the advanced-filter sheet themselves.
const LIST_STATE_KEY = 'agentCustomerListState';
const goToFilteredList = (navigate, { status = '', paymentType = '' }) => {
  sessionStorage.setItem(LIST_STATE_KEY, JSON.stringify({
    filter: '', search: '', collectionMode: false,
    adv: { status, area: [], dueDate: '', paymentType },
  }));
  navigate('/agent');
};

// "Collections" tab — the agent's collection dashboard: totals,
// recent collection history, and unresolved Collection Pending customers with reasons.
export default function AgentSummaryPage() {
  const { setHeader } = useOutletContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('summary'); // summary | history | pending

  useEffect(() => { setHeader({ title: 'Collections', showBack: false }); }, [setHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await agentAppAPI.getSummary();
      setData(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>;

  const pendingCount = (data.collectionPending || []).length;

  return (
    <div style={{ padding: 14 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[
          { key: 'summary', label: 'Summary' },
          { key: 'history', label: 'History' },
          { key: 'pending', label: `Pending${pendingCount ? ` (${pendingCount})` : ''}` },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              flex: 1, padding: '9px 0', borderRadius: 20, fontSize: '.85rem', fontWeight: 700,
              border: '1px solid var(--border)', cursor: 'pointer',
              background: tab === t.key ? 'var(--accent)' : 'var(--bg-card)',
              color: tab === t.key ? '#fff' : 'var(--text)',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'summary' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
            {data.monthlyTotalCollection && (
              <StatCard title="Monthly Total Collection" amount={data.monthlyTotalCollection.amount} sub={`From ${data.monthlyTotalCollection.customers} Customers`} />
            )}
            <StatCard title="Today's Payment Collected" amount={data.todaysPaymentCollected.amount} sub={`From ${data.todaysPaymentCollected.customers} Customers`} />
            <StatCard title="Total Pending Amount" amount={data.totalPendingAmount.amount} sub={`From ${data.totalPendingAmount.customers} Customers`}
              onClick={() => goToFilteredList(navigate, { status: 'active', paymentType: 'unpaid' })} />
          </div>

          <SectionTitle>Customer Summary</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <StatCard title="Total Customers" amount={data.totalCustomers} isCount
              onClick={() => goToFilteredList(navigate, { status: 'active' })} />
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700, fontSize: '.85rem', borderBottom: '2px solid var(--accent)', paddingBottom: 6, marginBottom: 6 }}>Area Wise Subscription</div>
              {data.areaWiseSubscription.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>No areas assigned</div>
              ) : (
                data.areaWiseSubscription.map((a) => (
                  <div key={a.area} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', padding: '2px 0' }}>
                    <span>{a.area}</span><span style={{ fontWeight: 700 }}>{a.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {tab === 'history' && (
        (data.collectionHistory || []).length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No collections recorded yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.collectionHistory.map((h) => (
              <div key={h.paymentId} onClick={() => navigate(`/agent/receipt/${h.paymentId}`)}
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '.9rem' }}>{h.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>
                    {fmtDate(h.date)} · {h.method?.toUpperCase()}{h.area ? ` · ${h.area}` : ''} · {h.receiptNumber}
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: '#16a34a', flexShrink: 0 }}>₹{fmt(h.amount)}</div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'pending' && (
        (data.collectionPending || []).length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No pending collections 🎉</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.collectionPending.map((p) => (
              <div key={p.customerId} onClick={() => navigate(`/agent/customer/${p.customerId}`)}
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: '3px solid #f59e0b', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '.9rem' }}>{p.name}</div>
                  <div style={{ color: '#b45309', fontSize: '.78rem', fontWeight: 600 }}>⚠ {p.reason}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '.72rem' }}>
                    {p.area}{p.recordedAt ? ` · ${fmtDate(p.recordedAt)}` : ''}
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: '#dc2626', flexShrink: 0 }}>₹{fmt(p.due)}</div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ color: 'var(--accent)', fontWeight: 700, marginBottom: 8, fontSize: '.9rem' }}>{children}</div>;
}

function StatCard({ title, amount, sub, isCount, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, textAlign: 'center',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontWeight: 700, fontSize: '.78rem', borderBottom: '2px solid var(--accent)', paddingBottom: 6, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{isCount ? amount : `₹${fmt(amount)}`}</div>
      {sub && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}
